/* eslint-disable no-control-regex */
import React, {useEffect, useState} from 'react'
import {GroupSmsForm, GroupSmsFormInitValues as initialValues} from '../SMSBroadCast/SettingsModel'
import * as Yup from 'yup'
import {FormikProps, useFormik} from 'formik'
import {Button} from 'react-bootstrap'
import GroupSMSModal from './Modal/GroupSMSModal'
import {Link} from 'react-router-dom'
import Select from 'react-select'
import useSenderIdsOptions, {ISenderIdsOption} from '../../hooks/useSenderIdsOptions'
import clsx from 'clsx'
import {useAuth} from '../auth'
import SmsTemplateModal from './Modal/SmsTemplateModal'
import useContactGroupOptions from '../../hooks/useContactGroupOptions'
import {useThemeMode} from '../../../_metronic/partials'
import {CapitalizeFirstLetter} from '../Users/AddressForm'
import useSms from './hook/useSms'
import {TemplateText, VarInputs} from './core/_model'
import {getCurrentTimestamp} from './core/helper'
import SelectSender from './SelectSender'
import OptimizedSelectBox from '../../utils/components/OptimizedSelectBox'
import FlatPickrDateTime from '../../utils/components/FlatPickrDateTime'

const groupSmsSchema = Yup.object().shape({
  cm_name: Yup.string().required('Campaign name is required.'),
  senderid: Yup.object().required('Sender Id is required'),
  groupname: Yup.string().required('Group Id is required'),
  sms_content: Yup.string().required('SMS content is required.'),
})

const GroupSMS: React.FC = () => {
  const {currentUser} = useAuth()
  const {smsState, handleSmsContentChange, handleSmsTypeChange, backToCountDefault} = useSms()
  const {group} = useContactGroupOptions()

  const [loading, setLoading] = useState(false)
  const [formData, setFormData] = useState<GroupSmsForm>(initialValues)
  const [defaultSender, setDefaultSender] = useState<ISenderIdsOption | null>(null)
  const [disableDateTime, setDisableDateTime] = useState<Boolean>(true)
  const [templateText, setTemplateText] = useState<TemplateText>({
    isUse: false,
    smsText: '',
    isSmartTemplate: 0,
  })
  const [isCreated, setIsCreated] = useState(false)
  const [smartVarField, setSmartVarField] = useState<string[]>([])
  const [varInputs, setVarInputs] = useState<VarInputs | undefined>(undefined)

  const providedCmName = `${currentUser?.user_full_name}- ${getCurrentTimestamp()}`

  const formik: FormikProps<GroupSmsForm> = useFormik({
    initialValues,
    validationSchema: groupSmsSchema,
    onSubmit: (values) => {
      console.log(values)
      setFormData({
        ...values,
        sms_count: smsState.noOfSms,
        smsTextCharacterCount: smsState.characterCount,
        sms_type: smsState.smsType,
      })
    },
  })

  const handleCancel = () => {
    formik.resetForm()
    formik.setFieldValue('cm_name', providedCmName)
    formik.setFieldValue('senderid', defaultSender)
    setIsCreated(false)
    setFormData(initialValues)
    backToCountDefault()
    setSmartVarField([])
  }

  if (isCreated) {
    handleCancel()
  }

  if (templateText.isUse) {
    formik.setFieldValue('sms_content', templateText.smsText)
    setTemplateText({
      isUse: false,
      smsText: templateText.smsText,
      isSmartTemplate: templateText.isSmartTemplate,
    })
  }

  const smartTemplate = (text: string) => {
    const regex = /#(.*?)#/g
    const matches = []
    let match

    // Loop over all matches and push the capturing group into the matches array
    while ((match = regex.exec(text)) !== null) {
      matches.push(match[1])
    }

    setSmartVarField(matches)

    const dynamicInputs = matches.reduce<Record<string, string>>((acc, match) => {
      acc[match] = ''
      return acc
    }, {})

    setVarInputs(dynamicInputs)
  }

  const handleVarFieldsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const {name, value} = event.target
    setVarInputs((prevData: any) => ({
      ...prevData,
      [name]: value,
    }))
  }

  const onSelectGroupname = (id: number) => {
    const selectedGroup = group.find((grp) => grp.id === id)

    if (selectedGroup) {
      formik.setFieldValue('groupid', selectedGroup)
    }
  }

  useEffect(() => {
    if (templateText.isSmartTemplate) {
      smartTemplate(templateText.smsText)
    } else {
      setSmartVarField([])
      setVarInputs(undefined)
    }
  }, [templateText])

  useEffect(() => {
    document.title = 'Group SMS'
    formik.setFieldValue('cm_name', providedCmName)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div className='card mb-5 mb-xl-10'>
      <div className='card-header border-0 cursor-pointer'>
        <div className='card-title m-0'>
          <h3 className='er m-0'>
            Group SMS <span className='form-text'>:: Send SMS To Contact Group</span>
          </h3>
        </div>
      </div>

      <div>
        <form onSubmit={formik.handleSubmit} noValidate className='form'>
          <div className='card-body border-top p-9'>
            <div className='row mb-4'>
              <label className='col-lg-4 col-form-label  fs-6'>
                <span className='required'>Campaign Name</span>
              </label>

              <div className='col-lg-8 fv-row'>
                <input
                  name='cm_name'
                  placeholder='Campaign Name'
                  className={clsx(
                    'form-control form-control-lg custom-input',
                    {'is-invalid': formik.touched.cm_name && formik.errors.cm_name},
                    {
                      'is-valid': formik.touched.cm_name && !formik.errors.cm_name,
                    }
                  )}
                  value={formik.values.cm_name}
                  onChange={formik.handleChange}
                />
                {formik.touched.cm_name && formik.errors.cm_name && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>{formik.errors.cm_name}</div>
                  </div>
                )}
              </div>
            </div>

            <div className='row mb-4'>
              <label className='col-lg-4 col-form-label  fs-6'>
                <span className='required'>Select Sender ID</span>
              </label>

              <div className='col-lg-8 fv-row'>
                <SelectSender formik={formik} setDefaultSender={setDefaultSender} />

                {formik.touched.senderid && formik.errors.senderid && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>{formik.errors.senderid}</div>
                  </div>
                )}
              </div>
            </div>

            <div className='row mb-4'>
              <label className='col-lg-4 col-form-label  fs-6'>
                <span className='required'>Group ID</span>
              </label>

              <div className='col-lg-8 fv-row'>
                <OptimizedSelectBox
                  placeholder='Select contact group'
                  options={group}
                  fieldValue='groupname'
                  formik={formik}
                  setSelect={onSelectGroupname}
                />

                {formik.touched.groupname && formik.errors.groupname && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>{formik.errors.groupname}</div>
                  </div>
                )}
              </div>
            </div>

            <div className='row mb-6'>
              <div className='row mb-6'>
                <label className='col-lg-4 col-form-label  fs-6'>Select SMS Type</label>

                <div className='col-lg-8 fv-row'>
                  <div className='d-flex align-items-center mt-3'>
                    <label className='form-check form-check-inline form-check-solid me-5'>
                      <input
                        className='form-check-input'
                        name='sms_type'
                        type='radio'
                        value={0}
                        checked={smsState.smsType === 0}
                        onChange={(e) => {
                          handleSmsTypeChange(e)
                        }}
                      />
                      <span className=' ps-2 fs-6'>Text</span>
                    </label>

                    <label className='form-check form-check-inline form-check-solid'>
                      <input
                        className='form-check-input'
                        name='sms_type'
                        type='radio'
                        value={1}
                        checked={smsState.smsType === 1}
                        onChange={(e) => {
                          handleSmsTypeChange(e)
                        }}
                      />
                      <span className=' ps-2 fs-6'>UniCode</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>

            <div className='row mb-6'>
              <label className='col-lg-4 col-form-label required  fs-6'>Enter Sms Content</label>
              <div className='col-lg-8 fv-row-3'>
                <textarea
                  name='sms_content'
                  className={clsx(
                    'form-control form-control-lg custom-input',
                    {'is-invalid': formik.touched.sms_content && formik.errors.sms_content},
                    {
                      'is-valid': formik.touched.sms_content && !formik.errors.sms_content,
                    }
                  )}
                  placeholder='SMS Content'
                  rows={5}
                  style={{resize: 'vertical', fontSize: '1.0rem'}}
                  value={formik.values.sms_content}
                  onChange={(e) => {
                    handleSmsContentChange(e)
                    formik.handleChange(e)
                    if (!smsState.characterCount) {
                      setSmartVarField([])
                      setVarInputs(undefined)
                    }
                  }}
                ></textarea>
                {formik.touched.sms_content && formik.errors.sms_content && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>{formik.errors.sms_content}</div>
                  </div>
                )}
                <div className='form-text'>
                  {smsState.characterCount} Characters | {smsState.noOfSms} SMS |{' '}
                  {smsState.noOfSms > 1 ? smsState.charPerSmsForLong : smsState.charPerSms} Char/SMS{' '}
                  <span>
                    <button
                      className='px-4 rounded border-0 bg-success fw-semibold text-white'
                      type='button'
                      data-bs-toggle='modal'
                      data-bs-target='#kt_modal_3'
                    >
                      Insert SMS Template
                    </button>
                  </span>{' '}
                </div>
              </div>
            </div>

            {templateText.isSmartTemplate && varInputs
              ? smartVarField.map((field: string, index: number) => {
                  return (
                    <div className='row mb-4' key={index}>
                      <label className='col-lg-4 col-form-label fs-6'>
                        <span className='required'>{CapitalizeFirstLetter(field)}</span>
                      </label>
                      <div className='col-lg-8 fv-row'>
                        <input
                          name={field}
                          placeholder={`Enter ${CapitalizeFirstLetter(field)}`}
                          className={clsx(
                            'form-control form-control-lg form-control-solid',
                            {'is-invalid': varInputs[field]! === ''},
                            {
                              'is-valid': varInputs[field],
                            }
                          )}
                          value={varInputs[field]}
                          onChange={(e) => handleVarFieldsChange(e)}
                        />
                      </div>
                    </div>
                  )
                })
              : null}

            <SmsTemplateModal setTemplateText={setTemplateText} isSmartTemplate={0} />

            <div className='row mb-6'>
              <label className='col-lg-4 col-form-label fs-6 required'>Schedule SMS</label>
              <div className='col-lg-8 fv-row'>
                <div className='d-flex align-items-center mt-3'>
                  <label className='form-check form-check-inline form-check-solid me-5'>
                    <input
                      className='form-check-input'
                      name='sms_schedule'
                      type='radio'
                      value='now'
                      checked={formik.values.sms_schedule === 'now'}
                      onChange={(e) => {
                        formik.setFieldValue('sms_schedule', 'now')
                        setDisableDateTime(true)
                      }}
                    />
                    <span className='ps-2 fs-6'>Send Now</span>
                  </label>
                  <label className='form-check form-check-inline form-check-solid'>
                    <input
                      className='form-check-input'
                      name='sms_schedule'
                      type='radio'
                      value='later'
                      checked={formik.values.sms_schedule === 'later'}
                      onChange={() => {
                        formik.setFieldValue('sms_schedule', 'later')
                      }}
                    />
                    <span className='ps-2 fs-6'>Send Later</span>
                  </label>
                </div>

                <div className='col-lg-8 mt-4'>
                  {formik.values.sms_schedule === 'later' ? (
                    <div id='sms_schedule' className='sms-schedule'>
                      <FlatPickrDateTime
                        placeholder='Click to select date time'
                        fieldName='schedule_date'
                        formik={formik}
                        container='sms_schedule'
                        dateformat='Y-m-d h:i K'
                      />
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          </div>

          <div className='card-footer d-flex justify-content-end py-6 px-9'>
            <button
              type='button'
              className='btn btn-danger me-2'
              onClick={handleCancel}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type='submit'
              className='btn btn-primary'
              disabled={loading}
              data-bs-toggle='modal'
              data-bs-target='#kt_modal_1'
            >
              {!loading && 'Review'}
              {loading && (
                <span className='indicator-progress' style={{display: 'block'}}>
                  Please wait...{' '}
                  <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                </span>
              )}
            </button>
            <GroupSMSModal
              formData={formData}
              setIsCreated={setIsCreated}
              setLoading={setLoading}
              formik={formik}
              varInputs={varInputs}
            />
          </div>
        </form>
      </div>
    </div>
  )
}

export {GroupSMS}
